import React, { useState, useEffect } from 'react';
import { 
  CreditCard, Search, CheckCircle2, XCircle, Clock, 
  Eye, QrCode, Download, Printer, Filter, Calendar,
  DollarSign, User, Package, AlertCircle, Check, Banknote, Image, RefreshCw
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import QRCode from 'react-qr-code';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PaymentConfirmationPortal = () => {
  const [activeTab, setActiveTab] = useState('etransfer'); // 'all', 'etransfer'
  const [etransferOrders, setEtransferOrders] = useState([]);
  const [loadingEtransfer, setLoadingEtransfer] = useState(false);
  const [selectedEtransfer, setSelectedEtransfer] = useState(null);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [screenshotModal, setScreenshotModal] = useState(null);

  // All payments state (mock for demo)
  const [payments, setPayments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showQRModal, setShowQRModal] = useState(false);

  const token = localStorage.getItem('auth_token');

  // Fetch pending e-Transfer orders
  const fetchEtransferOrders = async () => {
    setLoadingEtransfer(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/etransfer-pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEtransferOrders(data);
      } else {
        toast.error('Failed to fetch e-Transfer orders');
      }
    } catch (error) {
      console.error('Error fetching e-Transfer orders:', error);
      toast.error('Error fetching e-Transfer orders');
    } finally {
      setLoadingEtransfer(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'etransfer') {
      fetchEtransferOrders();
    }
  }, [activeTab]);

  // Verify e-Transfer
  const verifyEtransfer = async (orderId, approved) => {
    setVerifying(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/verify-etransfer/${orderId}?approved=${approved}&notes=${encodeURIComponent(verificationNotes)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(approved ? 'E-Transfer verified successfully!' : 'E-Transfer payment rejected');
        setSelectedEtransfer(null);
        setVerificationNotes('');
        fetchEtransferOrders();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to verify payment');
      }
    } catch (error) {
      toast.error('Error verifying payment');
    } finally {
      setVerifying(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'confirmed':
      case 'payment_confirmed':
        return <Badge className="bg-green-100 text-green-700">Confirmed</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">Pending</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rejected</Badge>;
      case 'awaiting_delivery':
        return <Badge className="bg-blue-100 text-blue-700">Awaiting Delivery</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-blue-500" />
            Payment Confirmation Portal
          </h1>
          <p className="text-gray-500 mt-1">Review and verify customer payments</p>
        </div>
        <div className="flex gap-3">
          <Badge className="bg-yellow-100 text-yellow-700 px-4 py-2" data-testid="pending-count">
            {etransferOrders.length} E-Transfer Pending
          </Badge>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTab === 'etransfer' ? 'default' : 'outline'}
          onClick={() => setActiveTab('etransfer')}
          className="flex items-center gap-2"
          data-testid="etransfer-tab"
        >
          <Banknote className="w-4 h-4" />
          E-Transfer Verification
        </Button>
        <Button
          variant={activeTab === 'all' ? 'default' : 'outline'}
          onClick={() => setActiveTab('all')}
          className="flex items-center gap-2"
          data-testid="all-payments-tab"
        >
          <CreditCard className="w-4 h-4" />
          All Payments
        </Button>
      </div>

      {/* E-Transfer Verification Tab */}
      {activeTab === 'etransfer' && (
        <div>
          {/* Refresh Button */}
          <div className="flex justify-end mb-4">
            <Button 
              variant="outline" 
              onClick={fetchEtransferOrders}
              disabled={loadingEtransfer}
              data-testid="refresh-etransfer"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loadingEtransfer ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loadingEtransfer ? (
            <Card className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-blue-500 mb-4" />
              <p className="text-gray-500">Loading pending e-Transfer orders...</p>
            </Card>
          ) : etransferOrders.length === 0 ? (
            <Card className="p-8 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-800">All Caught Up!</h3>
              <p className="text-gray-500">No e-Transfer payments pending verification.</p>
            </Card>
          ) : (
            <div className="grid gap-4">
              {etransferOrders.map((order) => (
                <Card key={order.id} className="p-6" data-testid={`etransfer-order-${order.id}`}>
                  <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                    {/* Order Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-4">
                        <h3 className="text-lg font-bold text-gray-800">
                          Order #{order.order_number || order.id.slice(0,8).toUpperCase()}
                        </h3>
                        <Badge className="bg-yellow-100 text-yellow-700">
                          <Clock className="w-3 h-3 mr-1" />
                          Pending Verification
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-500">Customer</p>
                          <p className="font-medium">{order.delivery_info?.name || 'N/A'}</p>
                          <p className="text-sm text-gray-500">{order.delivery_info?.email || ''}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Order Total</p>
                          <p className="font-bold text-lg text-green-600">{formatCurrency(order.total)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Order Date</p>
                          <p className="font-medium">{formatDate(order.created_at)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Delivery Zone</p>
                          <p className="font-medium">{order.delivery_zone || 'N/A'}</p>
                        </div>
                      </div>

                      {/* E-Transfer Details */}
                      <div className="bg-blue-50 rounded-lg p-4 mb-4">
                        <h4 className="font-semibold text-blue-800 mb-2 flex items-center gap-2">
                          <Banknote className="w-4 h-4" />
                          E-Transfer Information
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-blue-600">Confirmation Number</p>
                            <p className="font-mono font-bold text-lg text-blue-900">
                              {order.etransfer_confirmation || 'Not provided'}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-blue-600">Screenshot</p>
                            {order.etransfer_screenshot ? (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setScreenshotModal(order)}
                                className="mt-1"
                                data-testid={`view-screenshot-${order.id}`}
                              >
                                <Image className="w-4 h-4 mr-2" />
                                View Screenshot
                              </Button>
                            ) : (
                              <p className="text-gray-500">No screenshot uploaded</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Order Items Summary */}
                      <div className="text-sm text-gray-600">
                        <p className="font-medium mb-1">Order Items ({order.items?.length || 0} items):</p>
                        <div className="max-h-24 overflow-y-auto">
                          {order.items?.slice(0, 3).map((item, idx) => (
                            <p key={idx} className="text-gray-500">
                              {item.quantity}x {item.name} - {formatCurrency(item.price * item.quantity)}
                            </p>
                          ))}
                          {order.items?.length > 3 && (
                            <p className="text-gray-400 italic">+{order.items.length - 3} more items...</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Action Panel */}
                    <div className="lg:w-72 bg-gray-50 rounded-lg p-4">
                      <h4 className="font-semibold text-gray-800 mb-3">Verification Actions</h4>
                      
                      <Textarea
                        placeholder="Add notes (optional)..."
                        value={selectedEtransfer?.id === order.id ? verificationNotes : ''}
                        onChange={(e) => {
                          setSelectedEtransfer(order);
                          setVerificationNotes(e.target.value);
                        }}
                        className="mb-3"
                        rows={2}
                        data-testid={`notes-${order.id}`}
                      />

                      <div className="flex flex-col gap-2">
                        <Button
                          onClick={() => {
                            setSelectedEtransfer(order);
                            verifyEtransfer(order.id, true);
                          }}
                          disabled={verifying}
                          className="w-full bg-green-500 hover:bg-green-600 text-white"
                          data-testid={`approve-${order.id}`}
                        >
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          {verifying && selectedEtransfer?.id === order.id ? 'Processing...' : 'Approve Payment'}
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setSelectedEtransfer(order);
                            verifyEtransfer(order.id, false);
                          }}
                          disabled={verifying}
                          className="w-full text-red-600 hover:bg-red-50 border-red-200"
                          data-testid={`reject-${order.id}`}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          {verifying && selectedEtransfer?.id === order.id ? 'Processing...' : 'Reject Payment'}
                        </Button>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* All Payments Tab (Placeholder/Demo) */}
      {activeTab === 'all' && (
        <Card className="p-8 text-center">
          <CreditCard className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800">All Payments View</h3>
          <p className="text-gray-500 mb-4">
            This section shows all payment transactions. Currently viewing e-Transfer verification is prioritized.
          </p>
          <Button variant="outline" onClick={() => setActiveTab('etransfer')}>
            Go to E-Transfer Verification
          </Button>
        </Card>
      )}

      {/* Screenshot Modal */}
      {screenshotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={() => setScreenshotModal(null)}>
          <div className="bg-white rounded-xl p-6 max-w-3xl max-h-[90vh] overflow-auto m-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">
                E-Transfer Screenshot - Order #{screenshotModal.order_number || screenshotModal.id.slice(0,8).toUpperCase()}
              </h3>
              <Button variant="ghost" size="sm" onClick={() => setScreenshotModal(null)}>
                <XCircle className="w-5 h-5" />
              </Button>
            </div>
            
            <div className="bg-gray-100 rounded-lg p-2 mb-4">
              <p className="text-sm text-gray-600 mb-2">
                <strong>Confirmation #:</strong> {screenshotModal.etransfer_confirmation}
              </p>
              <p className="text-sm text-gray-600">
                <strong>Amount:</strong> {formatCurrency(screenshotModal.total)}
              </p>
            </div>

            {screenshotModal.etransfer_screenshot ? (
              <img
                src={screenshotModal.etransfer_screenshot}
                alt="E-Transfer Screenshot"
                className="max-w-full rounded-lg border border-gray-200"
                data-testid="screenshot-image"
              />
            ) : (
              <div className="bg-gray-100 rounded-lg p-8 text-center">
                <Image className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No screenshot available</p>
              </div>
            )}

            <div className="flex gap-3 mt-6">
              <Button variant="outline" className="flex-1" onClick={() => setScreenshotModal(null)}>
                Close
              </Button>
              <Button
                className="flex-1 bg-green-500 hover:bg-green-600 text-white"
                onClick={() => {
                  setSelectedEtransfer(screenshotModal);
                  verifyEtransfer(screenshotModal.id, true);
                  setScreenshotModal(null);
                }}
                disabled={verifying}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Approve
              </Button>
              <Button
                variant="outline"
                className="flex-1 text-red-600 hover:bg-red-50"
                onClick={() => {
                  setSelectedEtransfer(screenshotModal);
                  verifyEtransfer(screenshotModal.id, false);
                  setScreenshotModal(null);
                }}
                disabled={verifying}
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentConfirmationPortal;
